import { db } from './firebase';
import { collection, query, where, onSnapshot, orderBy, limit } from 'firebase/firestore';

/**
 * 📡 FirestoreSubscriptionManager
 * Merkezi Firestore Realtime Yayın-Abone (Pub/Sub) Yöneticisi.
 * Aynı koleksiyona birden fazla onSnapshot bağlantısı açılmasını engeller.
 * Sadece 1 canlı bağlantı açıp tüm dinleyici bileşenlere bellekten dağıtır.
 */
class FirestoreSubscriptionManager {
  constructor() {
    this.subscriptions = new Map(); // Active Firestore onSnapshot unsubscribe fns
    this.subscribers = new Map();   // Callback arrays keyed by topic
    this.cachedData = new Map();    // Latest snapshot data by topic
  }

  /**
   * Bir konuya (topic) abone ol.
   * Eğer bu konu için Firestore bağlantısı yoksa otomatik başlatır.
   */
  subscribe(topic, queryConfig, callback) {
    if (!topic || typeof callback !== 'function') return () => {};

    // Abone listesine ekle
    if (!this.subscribers.has(topic)) {
      this.subscribers.set(topic, new Set());
    }
    this.subscribers.get(topic).add(callback);

    // Eğer elimizde önceden çekilmiş cache varsa anında teslim et
    if (this.cachedData.has(topic)) {
      callback(this.cachedData.get(topic));
    }

    // Firestore canlı dinleyicisi henüz kurulmadıysa kur
    if (!this.subscriptions.has(topic) && queryConfig) {
      this.startFirestoreListener(topic, queryConfig);
    }

    // Unsubscribe fonksiyonu döndür
    return () => {
      const topicSubscribers = this.subscribers.get(topic);
      if (topicSubscribers) {
        topicSubscribers.delete(callback);
        // Eğer bu konuda hiç abone kalmadıysa Firestore dinleyicisini kapat
        if (topicSubscribers.size === 0) {
          this.stopFirestoreListener(topic);
        }
      }
    };
  }

  /**
   * Firestore dinleyicisini başlatır (Tek bağlantı)
   */
  startFirestoreListener(topic, { ref, constraints = [], transform }) {
    try {
      const q = constraints.length > 0 ? query(ref, ...constraints) : ref;

      console.log(`📡 [SubscriptionManager] Starting Firestore listener for: ${topic}`);

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const data = transform ? transform(snapshot) : snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        
        // Cache'i güncelle
        this.cachedData.set(topic, data);

        // Tüm abonelere dağıt
        const callbacks = this.subscribers.get(topic);
        if (callbacks) {
          callbacks.forEach(cb => {
            try {
              cb(data);
            } catch (err) {
              console.error(`❌ [SubscriptionManager] Error in subscriber callback for ${topic}:`, err);
            }
          });
        }
      }, (error) => {
        if (error.code !== 'permission-denied') {
          console.error(`❌ [SubscriptionManager] Firestore error on ${topic}:`, error);
        }
      });

      this.subscriptions.set(topic, unsubscribe);
    } catch (err) {
      console.error(`❌ [SubscriptionManager] Failed to start listener for ${topic}:`, err);
    }
  }

  /**
   * Kapsamlı temizlik — Belirli bir konunun dinleyicisini kapatır
   */
  stopFirestoreListener(topic) {
    const unsub = this.subscriptions.get(topic);
    if (unsub) {
      unsub();
      this.subscriptions.delete(topic);
      this.cachedData.delete(topic);
      console.log(`🔴 [SubscriptionManager] Stopped Firestore listener for: ${topic}`);
    }
  }

  /**
   * İşletme değiştiğinde tüm aktif dinleyicileri kapatır
   */
  stopAll() {
    this.subscriptions.forEach((unsub, topic) => {
      try { unsub(); } catch (e) {}
      console.log(`🔴 [SubscriptionManager] Stopped: ${topic}`);
    });
    this.subscriptions.clear();
    this.subscribers.clear();
    this.cachedData.clear();
  }
}

export const subscriptionManager = new FirestoreSubscriptionManager();
export default subscriptionManager;
